import { motion } from "framer-motion";
import { HANDLE_COLLECTIONS, HARDWARE_FINISHES } from "@/lib/catalog";

export default function HardwareSection() {
  return (
    <section
      id="hardware"
      className="relative bg-brand-green text-brand-bone py-24 md:py-32"
      data-testid="hardware-section"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-[0.68rem] tracking-[0.28em] uppercase text-brand-gold font-medium">
            04 · Hardware
          </span>
          <span className="h-px w-16 bg-brand-gold/40" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-14 md:mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="md:col-span-7 text-brand-bone font-light leading-[1.05] tracking-[-0.02em] text-3xl md:text-4xl lg:text-5xl"
          >
            Handles inspired
            <span className="block text-brand-gold">by capitals.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="md:col-span-5 text-brand-bone/75 leading-relaxed font-light"
          >
            Eight sample handle collections — Madrid, Rome, Barcelona, Milan, Florence, Naples,
            Valencia, Turin — paired with a soft magnetic close for a quiet, elevated experience.
          </motion.p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-x-6 gap-y-10 md:gap-y-14 max-w-[1080px] mx-auto">
          {HANDLE_COLLECTIONS.map((h, i) => (
            <motion.div
              key={h.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.06 }}
              className="group"
              data-testid={`handle-${h.name.toLowerCase()}`}
            >
              <div className="relative aspect-[4/3] flex items-center justify-center">
                <img
                  src={h.image}
                  alt={`${h.name} handle in ${h.finish}`}
                  data-testid={`handle-image-${h.name.toLowerCase()}`}
                  className="max-h-[75%] max-w-[75%] object-contain transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="mt-3 text-center">
                <p className="text-brand-bone text-base md:text-lg font-normal tracking-[-0.01em]">{h.name}</p>
                <p className="text-[0.55rem] tracking-[0.24em] uppercase text-brand-gold/90 mt-1 font-medium">
                  {h.finish}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Finish rail */}
        <div className="mt-20 md:mt-28 border-t border-brand-gold/20 pt-10">
          <div className="flex items-center gap-4 mb-8">
            <span className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-gold">
              Hardware Finishes
            </span>
            <span className="flex-1 h-px bg-brand-bone/15" />
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-4 gap-y-6 max-w-[1080px] mx-auto" data-testid="hardware-finishes">
            {HARDWARE_FINISHES.map((f) => (
              <div
                key={f.name}
                className="flex flex-col items-center text-center"
                data-testid={`finish-${f.name.replace(/\W+/g, "-").toLowerCase()}`}
              >
                <img
                  src={f.image}
                  alt={f.name}
                  data-testid={`finish-image-${f.name.replace(/\W+/g, "-").toLowerCase()}`}
                  className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 object-contain border border-brand-bone/15 shadow-sm"
                  loading="lazy"
                />
                <span data-testid={`finish-label-${f.name.replace(/\W+/g, "-").toLowerCase()}`} className="mt-2 text-[0.58rem] leading-tight tracking-[0.14em] uppercase text-brand-bone/85 max-w-[7rem]">
                  {f.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
